import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hint } from '@/components/ui/hint';
import { HELP } from '@/lib/helpText';
import {
  PanelLeftClose, PanelLeftOpen, Plus, MessageCircle, Compass, Repeat, Crown, CalendarCheck2,
} from 'lucide-react';

const MODE_META = {
  explore: { label: 'Explore', icon: Compass, chip: 'bg-indigo-50 text-indigo-600 ring-indigo-100' },
  ai_tutor: { label: 'Exam Mode', icon: Crown, chip: 'bg-fuchsia-50 text-fuchsia-600 ring-fuchsia-100' },
};

/** Left ribbon: new chat, current mode, history and quick links. */
export function StudySidebar({ mode, sessions = [], activeSessionId, onNewChat, onChangeMode, onOpenSession, onOpenHistory }) {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('gv_sidebar_collapsed') === '1'; } catch { return false; }
  });
  const toggle = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem('gv_sidebar_collapsed', next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  };
  const meta = MODE_META[mode] || MODE_META.explore;
  const ModeIcon = meta.icon;

  return (
    <aside
      data-testid="study-sidebar"
      className={`hidden md:flex flex-col flex-shrink-0 border-r border-slate-200/70 bg-white/92 backdrop-blur-md transition-[width] duration-200 ${collapsed ? 'w-[58px]' : 'w-60'}`}
    >
      <div className="flex items-center justify-between px-2.5 py-2.5">
        {!collapsed && <span className="text-[10px] font-heading uppercase tracking-widest text-slate-400 pl-1.5">Study</span>}
        <Hint text={HELP.study.collapse} side="right">
          <button data-testid="sidebar-toggle" onClick={toggle}
            className="w-8 h-8 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors">
            {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </Hint>
      </div>

      <div className="px-2 space-y-1.5">
        <Hint text={HELP.study.newChat} side="right" className="w-full">
          <button data-testid="sidebar-new-chat" onClick={onNewChat}
            className={`w-full flex items-center gap-2 rounded-xl bg-slate-900 text-white px-2.5 py-2 hover:bg-slate-800 transition-colors ${collapsed ? 'justify-center' : ''}`}>
            <Plus className="w-4 h-4 flex-shrink-0" />
            {!collapsed && <span className="font-heading text-sm">New chat</span>}
          </button>
        </Hint>
        <Hint text={HELP.study.changeMode} side="right" className="w-full">
          <button data-testid="sidebar-change-mode" onClick={onChangeMode}
            className={`w-full flex items-center gap-2 rounded-xl ring-1 px-2.5 py-2 transition-all hover:shadow-sm ${meta.chip} ${collapsed ? 'justify-center' : ''}`}>
            <ModeIcon className="w-4 h-4 flex-shrink-0" />
            {!collapsed && (
              <span className="min-w-0 text-left">
                <span className="block font-heading text-[13px] leading-tight truncate">{meta.label}</span>
                <span className="block text-[10px] opacity-70 font-body leading-tight">Mode Of Study</span>
              </span>
            )}
            {!collapsed && <Repeat className="w-3 h-3 ml-auto opacity-60" />}
          </button>
        </Hint>
        <Hint text="Your day-by-day study and exam plan" side="right" className="w-full">
          <button data-testid="sidebar-planner" onClick={() => navigate('/planner')}
            className={`w-full flex items-center gap-2 rounded-xl ring-1 ring-indigo-100 bg-indigo-50 text-indigo-600 px-2.5 py-2 transition-all hover:shadow-sm ${collapsed ? 'justify-center' : ''}`}>
            <CalendarCheck2 className="w-4 h-4 flex-shrink-0" />
            {!collapsed && (
              <span className="min-w-0 text-left">
                <span className="block font-heading text-[13px] leading-tight truncate">Study / Exam Planner</span>
                <span className="block text-[10px] opacity-70 font-body leading-tight">Today, week and roadmap</span>
              </span>
            )}
          </button>
        </Hint>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto mt-4 px-2 pb-3">
        {!collapsed && (
          <div className="flex items-center justify-between px-1.5 mb-1.5">
            <p className="text-[10px] font-heading uppercase tracking-widest text-slate-400">Recent</p>
            <button data-testid="sidebar-all-chats" onClick={onOpenHistory}
              className="text-[10px] font-heading text-blue-500 hover:underline">See all</button>
          </div>
        )}
        <div className="space-y-0.5">
          {sessions.map(s => (
            <Hint key={s.session_id} text={HELP.study.recentChat} side="right" className="w-full">
              <button data-testid={`sidebar-session-${s.session_id}`}
                onClick={() => onOpenSession?.(s.session_id)}
                className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors ${activeSessionId === s.session_id ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-100'} ${collapsed ? 'justify-center' : ''}`}>
                <MessageCircle className="w-3.5 h-3.5 flex-shrink-0 opacity-70" />
                {!collapsed && <span className="font-body text-xs truncate">{s.label || s.topic || s.subject || 'Ask me anything'}</span>}
              </button>
            </Hint>
          ))}
          {!collapsed && sessions.length === 0 && (
            <p className="text-[11px] text-slate-400 font-body px-1.5 py-1">No chats yet — say hello above.</p>
          )}
        </div>
      </div>

    </aside>
  );
}

export default StudySidebar;
